<script>
import Layout from '@layouts/main'
import { permissionMethods, authComputed } from '@state/helpers'
import _ from 'lodash'

export default {
  metaInfo: {
    title: 'Permissions',
    meta: [{ name: 'description', content: 'Permissions' }],
  },
  components: { Layout },
  data() {
    return {
      search: '',
      loadingError: null,
      formError: null,
      loading: true,
      dialog: false,
      pagination: {
        rowsPerPage: 25,
      },
      editedIndex: -1,
      items: [],
      totalData: 0,
      isWorking: false,
      selectedUserRole: null,
      userRoleItems: [],
      userRolesLoading: false,
      userRoleSearchTerm: null,
      userRoleError: null,
      headers: [
        {
          text: 'Name',
          value: 'role.name',
          sortable: false,
        },
        {
          text: 'Import Files',
          value: 'importFiles',
        },
        {
          text: 'Manage Settings',
          value: 'manageSettings',
        },
        {
          text: 'Manage Users',
          value: 'manageUsers',
        },
        {
          text: 'Manage Roles',
          value: 'manageRoles',
        },
        {
          text: 'Manage Permissions',
          value: 'managePermissions',
        },
        { text: 'Actions', value: 'role.name', sortable: false },
      ],
      editedItem: {
        roleId: null,
        userId: null,
        importFiles: false,
        manageSettings: false,
        manageUsers: false,
        manageRoles: false,
        managePermissions: false,
      },
      defaultItem: {
        roleId: null,
        userId: null,
        importFiles: false,
        manageSettings: false,
        manageUsers: false,
        manageRoles: false,
        managePermissions: false,
      },
    }
  },
  computed: {
    ...authComputed,
    formTitle() {
      return this.editedIndex === -1
        ? 'Create New Permissions'
        : `Edit ${this.items[this.editedIndex].user.username ||
            this.items[this.editedIndex].role.name} Permissions`
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getDataFromApi()
      },
      deep: true,
    },
    dialog(val) {
      val || this.close()
    },
    userRoleSearchTerm: {
      handler() {
        if (!this.userRoleSearchTerm) {
          this.userRoleItems = []
          return
        }

        this.searchForUsersRoles()
      },
    },
  },
  created() {
    this.getDataFromApi = _.debounce(this.getDataFromApi, 500)
    this.searchForUsersRoles = _.debounce(this.searchForUsersRoles, 500)
  },
  methods: {
    ...permissionMethods,
    getDataFromApi() {
      this.loading = true
      var vm = this
      return this.getPermissions({
        queryTerms: { ...this.pagination, filter: this.search },
      })
        .then(data => {
          vm.loading = false

          vm.totalData = data.totalCount
          vm.items = data.results
        })
        .catch(error => {
          vm.loading = false
          vm.loadingError = error
        })
    },
    save() {
      this.isWorking = true
      this.formError = null

      if (!this.editedItem.id) {
        if (!this.selectedUserRole) {
          this.$swal({
            title: 'Error!',
            text: 'You must choose a user or a role.',
            type: 'error',
            confirmButtonText: 'OK',
          })
          return
        }

        if (this.selectedUserRole.type === 'user') {
          this.editedItem.userId = this.selectedUserRole.id
        } else {
          this.editedItem.roleId = this.selectedUserRole.id
        }
      } else {
        this.editedItem.user = null
        this.editedItem.role = null
      }

      if (this.editedIndex !== -1) {
        // Update user
        this.updatePermission({ permission: this.editedItem })
          .then(response => {
            this.isWorking = false
            var permission = response.data
            var matches = this.items.filter(x => x.id === permission.id)
            if (matches.length) {
              var current = matches[0]
              var indexToReplace = this.items.indexOf(current)
              this.items[indexToReplace] = permission
            }
            this.dialog = false
          })
          .catch(error => {
            this.isWorking = false
            this.formError = error.response.data
          })
      } else {
        // Add new
        this.addPermission({ permission: this.editedItem })
          .then(response => {
            this.isWorking = false
            var permission = response.data
            this.items.unshift(permission)
            this.dialog = false
          })
          .catch(error => {
            this.isWorking = false
            this.formError = error.response.data
          })
      }
    },
    close() {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      }, 300)
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },
    permissionDisplayName(item) {
      if (item.user) {
        return `${item.user.firstName} ${item.user.lastName}`
      }

      if (item.role) {
        return item.role.name
      }

      return ''
    },
    searchForUsersRoles() {
      this.userRolesLoading = true
      this.userRoleItems = []
      this.userRoleSearch({ search: this.userRoleSearchTerm })
        .then(response => {
          this.userRolesLoading = false
          this.userRoleItems = response.data
        })
        .catch(error => {
          this.userRolesLoading = false
          this.userRoleError = error.response.data
        })
    },
  },
}
</script>

<template>
  <Layout>
    <v-container fluid grid-list-md ma-0>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>Permissions</v-toolbar-title>
        <v-divider class="mx-2" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" max-width="500px">
          <v-btn slot="activator" color="primary" dark class="mb-2">
            {{ formTitle }}
          </v-btn>
          <v-card>
            <v-card-title>
              <span class="headline">{{ formTitle }}</span>
            </v-card-title>

            <v-card-text>
              <v-container grid-list-md>
                <v-layout wrap>
                  <v-flex md12>
                    <v-combobox
                      v-if="!editedItem.id"
                      v-model="selectedUserRole"
                      :items="userRoleItems"
                      :loading="userRolesLoading"
                      :search-input.sync="userRoleSearchTerm"
                      item-text="name"
                      item-value="id"
                      label="Users/Roles"
                      placeholder="Start typing to Search"
                      hint="Choose a user/role"
                    ></v-combobox>
                  </v-flex>
                  <v-flex md12>
                    <v-checkbox
                      v-model="editedItem.importFiles"
                      label="Import Files"
                    ></v-checkbox>
                  </v-flex>
                  <v-flex md12>
                    <v-checkbox
                      v-model="editedItem.manageSettings"
                      label="Manage Settings"
                    ></v-checkbox>
                  </v-flex>
                  <v-flex md12>
                    <v-checkbox
                      v-model="editedItem.manageUsers"
                      label="Manage Users"
                    ></v-checkbox>
                  </v-flex>
                  <v-flex md12>
                    <v-checkbox
                      v-model="editedItem.manageRoles"
                      label="Manage Roles"
                    ></v-checkbox>
                  </v-flex>
                  <v-flex md12>
                    <v-checkbox
                      v-model="editedItem.managePermissions"
                      label="Manage Permissions"
                    ></v-checkbox>
                  </v-flex>
                  <v-alert :value="formError" type="error">
                    {{ formError }}
                  </v-alert>
                </v-layout>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" flat @click="close">Cancel</v-btn>
              <v-btn color="blue darken-1" flat @click="save">
                <BaseIcon v-if="isWorking" name="sync" spin />
                <span v-else>Save</span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-toolbar>
      <v-card>
        <v-card-title>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="items"
          :pagination.sync="pagination"
          :total-items="totalData"
          :loading="loading"
          :search="search"
          class="elevation-1"
        >
          <v-progress-linear
            slot="progress"
            color="blue"
            indeterminate
          ></v-progress-linear>
          <template slot="items" slot-scope="props">
            <td>
              <div :class="$style.verticalcenter">
                <v-icon v-if="props.item.user">mdi-account</v-icon>
                <v-icon v-else>mdi-account-multiple</v-icon>
                {{ permissionDisplayName(props.item) }}
              </div>
            </td>
            <td>
              <v-icon v-if="props.item.importFiles" color="green"
                >mdi-check</v-icon
              >
              <v-icon v-else color="red">mdi-close</v-icon>
            </td>
            <td>
              <v-icon v-if="props.item.manageSettings" color="green"
                >mdi-check</v-icon
              >
              <v-icon v-else color="red">mdi-close</v-icon>
            </td>
            <td>
              <v-icon v-if="props.item.manageUsers" color="green"
                >mdi-check</v-icon
              >
              <v-icon v-else color="red">mdi-close</v-icon>
            </td>
            <td>
              <v-icon v-if="props.item.manageRoles" color="green"
                >mdi-check</v-icon
              >
              <v-icon v-else color="red">mdi-close</v-icon>
            </td>
            <td>
              <v-icon v-if="props.item.managePermissions" color="green"
                >mdi-check</v-icon
              >
              <v-icon v-else color="red">mdi-close</v-icon>
            </td>
            <td>
              <v-icon small class="mr-2" @click="editItem(props.item)"
                >edit</v-icon
              >
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.clickable {
  cursor: pointer;
}

.verticalcenter {
  display: flex;
  align-items: center;
}
</style>
